<template lang="pug">
  #wellness-member-overview
    WtTitle(card-title) Your Progress
    WtLeaderboard.member-horizontal-bar-chart(
      title=""
      :sub-title="leaderboardSubTitle"
      :data="{ data: getLeaderboardById(challengeId).filter(member => member.id === $auth.user().id) }"
      isChallenge
      isPoints
      :loadedStatus="leaderboardLoaded"
      :height="150"
      :maxX="challenge.totalPointsPossible"
      :goalLine="challenge.totalPointsGoal"
      showPacer
      :pacerValue="pacerValue"
      :barPercentage="0.8"
      :showTooltipLabel="false"
    )
    .level.is-mobile.is-flex-wrap-wrap.has-background-light.p-4.has-border-radius-large(v-if="!pointsInChallengeLoading")
      //- v-if="isMetricOn(metric.name)"
      WellnessMemberOverviewMetricProgress.level-item(
        v-for="metric in availableMetrics"
        v-if="isMetricOn(metric.key)"
        :key="metric.key"
        :metricTitle="metric.title"
        :pointsTotalAwardedFor="metric.pointsTotalAwardedFor"
        :pointsPossibleFor="metric.pointsPossibleFor"
        :icon="metric.icon"
        :metricPercentage="metric.metricPercentage"
        :strokeColor="metric.strokeColor"
      )

</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import { pointsMixin } from "@/mixins/pointsMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import WtLeaderboard from "@@/WtLeaderboard/WtLeaderboard";
import WellnessMemberOverviewMetricProgress from "@@/Challenge/WellnessChallenge/WellnessMemberOverviewMetricProgress";

export default {
  name: "WellnessMemberOverview",

  components: {
    WtButton,
    WtTile,
    WtTitle,
    WtSvgIcon,
    WtLeaderboard,
    WellnessMemberOverviewMetricProgress
  },

  mixins: [dateUtilityMixin, pointsMixin],

  props: {
    pacerValue: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      pointsInChallengeLoading: false,
      membersPointsTotals: {}
    };
  },

  computed: {
    ...mapGetters("members", ["challenges"]),
    ...mapGetters("challenges", [
      "getChallengeById",
      "getLeaderboardById",
      "leaderboardLoaded"
    ]),

    availableMetrics() {
      return [
        {
          key: this.pointsForWellnessHeart,
          title: "Heart",
          icon: "wellness_member_overview/icon_heart",
          strokeColor: "#DF1F00", // $red
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessHeart
          ),
          pointsPossibleFor: this.pointsPossibleFor(
            this.pointsForWellnessHeart
          ),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessHeart),
          metricPercentage: this.metricPercentage(this.pointsForWellnessHeart)
        },
        {
          key: this.pointsForWellnessMindfulness,
          title: "Mindfulness",
          icon: "wellness_member_overview/icon_mindfulness",
          strokeColor: "#C799C2", // $purple-light
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessMindfulness
          ),
          pointsPossibleFor: this.pointsPossibleFor(
            this.pointsForWellnessMindfulness
          ),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessMindfulness),
          metricPercentage: this.metricPercentage(
            this.pointsForWellnessMindfulness
          )
        },
        {
          key: this.pointsForWellnessNutrition,
          title: "Nutrition",
          icon: "wellness_member_overview/icon_nutrition_apple",
          strokeColor: "#BB331D", // $red-dark
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessNutrition
          ),
          pointsPossibleFor: this.pointsPossibleFor(
            this.pointsForWellnessNutrition
          ),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessNutrition),
          metricPercentage: this.metricPercentage(
            this.pointsForWellnessNutrition
          )
        },
        {
          key: this.pointsForWellnessWater,
          title: "Water",
          icon: "wellness_member_overview/icon_water",
          strokeColor: "#0288D1", // $blue
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessWater
          ),
          pointsPossibleFor: this.pointsPossibleFor(
            this.pointsForWellnessWater
          ),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessWater),
          metricPercentage: this.metricPercentage(this.pointsForWellnessWater)
        },
        {
          key: "textandimage",
          title: "Journal Entry & Photos",
          icon: "wellness_member_overview/icon_journal",
          strokeColor: "#1F9E9E", // $teal
          pointsTotalAwardedFor: this.pointsTotalAwardedFor("textandimage"),
          pointsPossibleFor: this.pointsPossibleFor("textandimage"),
          isMetricOn: this.pointsPossibleFor("textandimage"),
          metricPercentage: this.metricPercentage("textandimage")
        },
        {
          key: this.pointsForWellnessSleep,
          title: "Sleep",
          icon: "wellness_member_overview/icon_sleep",
          strokeColor: "#17307D", // $navy-dark
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessSleep
          ),
          pointsPossibleFor: this.pointsPossibleFor(
            this.pointsForWellnessSleep
          ),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessSleep),
          metricPercentage: this.metricPercentage(this.pointsForWellnessSleep)
        },
        {
          key: this.pointsForWellnessWeight,
          title: "Weight",
          icon: "wellness_member_overview/icon_weight",
          strokeColor: "#EDC947", // $yellow
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessWeight
          ),
          pointsPossibleFor: this.pointsPossibleFor(
            this.pointsForWellnessWeight
          ),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessWeight),
          metricPercentage: this.metricPercentage(this.pointsForWellnessWeight)
        },
        {
          key: this.pointsForWellnessMood,
          title: "Mood",
          icon: "wellness_member_overview/icon_mood",
          strokeColor: "#388E3C", // $green
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForWellnessMood
          ),
          pointsPossibleFor: this.pointsPossibleFor(this.pointsForWellnessMood),
          isMetricOn: this.pointsPossibleFor(this.pointsForWellnessMood),
          metricPercentage: this.metricPercentage(this.pointsForWellnessMood)
        },
        {
          key: this.pointsForJournalSteps,
          title: "Physical",
          icon: "wellness_member_overview/icon_jumprope",
          strokeColor: "#9F5196", // $purple
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForJournalSteps
          ),
          pointsPossibleFor: this.pointsPossibleFor(this.pointsForJournalSteps),
          isMetricOn: this.pointsPossibleFor(this.pointsForJournalSteps),
          metricPercentage: this.metricPercentage(this.pointsForJournalSteps)
        },
        {
          key: this.pointsForQuestion,
          title: "Questions",
          icon: "wellness_member_overview/icon_questions",
          strokeColor: "#85C1E1", // $blue-light
          pointsTotalAwardedFor: this.pointsTotalAwardedFor(
            this.pointsForQuestion
          ),
          pointsPossibleFor: this.pointsPossibleFor(this.pointsForQuestion),
          isMetricOn: this.pointsPossibleFor(this.pointsForQuestion),
          metricPercentage: this.metricPercentage(this.pointsForQuestion)
        }
      ];
    },

    page() {
      return `Challenge${this.challengeId}`;
    },

    leaderboardSubTitle() {
      return "Total Points In Challenge";
    },

    challengeId() {
      return this.$route.params.challengeId;
    },

    challenge() {
      return this.getChallengeById(this.challengeId);
    },

    isCompleted() {
      const COMPLETED = 2;
      return (
        this.getChallengeById(this.$route.params.challengeId).status ===
        COMPLETED
      );
    },

    thisMembersPoints() {
      const leaderboard = this.getLeaderboardById(this.challengeId);
      const member = leaderboard.find(member => {
        return member.id === this.$auth.user().id;
      });
      return member ? member.points : null;
    },

    textAndImagePointsGoal() {
      return (
        this.pointsGoalFor(this.pointsForJournalText) +
        this.pointsGoalFor(this.pointsForJournalPhoto)
      );
    },

    textAndImagePointsPossible() {
      return (
        this.pointsPossibleFor(this.pointsForJournalText) +
        this.pointsPossibleFor(this.pointsForJournalPhoto)
      );
    },

    textAndImageTotalAwarded() {
      return (
        this.pointsTotalAwardedFor(this.pointsForJournalText) +
        this.pointsTotalAwardedFor(this.pointsForJournalPhoto)
      );
    },

    isJoinedChallenge() {
      const memberChallengeIds = this._.get(
        this.$auth.user(),
        "challenges",
        this["members/challenges"]
      ).map(item => item.id);

      return memberChallengeIds.includes(this.challenge.id);
    }
  },

  async mounted() {
    try {
      this.pointsInChallengeLoading = true;
      let response = await this.fetchPointsInChallenge({
        memberId: this.$auth.user().id,
        challengeId: this.challengeId
      });
      this.membersPointsTotals = this._.get(response, "data.data", {});
      // quick fix for if fetchPointsInChallenge returns an array we have issues since 'entries' is a reserved word
      // ensure empty is an object
      if (this._.isEmpty(this.membersPointsTotals)) {
        this.membersPointsTotals = {};
      }
      this.pointsInChallengeLoading = false;
    } catch (error) {
      this.membersPointsTotals = {};
    }
  },

  methods: {
    ...mapActions("members", ["fetchPointsInChallenge"]),

    isMetricOn(metric) {
      if (metric === "textandimage") {
        return (
          this.challenge.metricsTracking[this.pointsForJournalPhoto] ||
          this.challenge.metricsTracking[this.pointsForJournalText]
        );
      }
      return this.challenge.metricsTracking[metric];
    },

    pointsGoalFor(metric) {
      if (metric === "textandimage") {
        return this.textAndImagePointsGoal;
      }
      return this.challenge.pointsGoalsForEachMetric[metric];
    },

    pointsPossibleFor(metric) {
      if (metric === "textandimage") {
        return this.textAndImagePointsPossible;
      }
      return this.challenge.pointsPossibleForEachMetric[metric];
    },

    pointsTotalAwardedFor(metric) {
      if (!this.isJoinedChallenge) {
        return 0;
      }

      if (metric === "textandimage") {
        return this.textAndImageTotalAwarded;
      }
      return this._.get(this.membersPointsTotals, metric, 0);
    },

    formatPercentage(value) {
      if (!value) return 0;
      // !value since we dont' want to return 100 if the value has an error
      return value < 1 || !value ? Math.round(value * 100) : 100;
    },

    metricPercentage(metric) {
      if (!this.isJoinedChallenge) {
        return 0;
      }

      if (metric === "textandimage") {
        return this.formatPercentage(
          this.textAndImageTotalAwarded / this.textAndImagePointsPossible
        );
      }

      return this.formatPercentage(
        this.pointsTotalAwardedFor(metric) / this.pointsPossibleFor(metric)
      );
    }
  }
};
</script>

<style lang="sass" scoped>
#wellness-member-overview
  .has-border-radius-circle
    border-radius: 100%
</style>
